import type {HistoryDomainAdapterRegistry} from '../domain';
import {mergeDirtyScopes} from '../DirtyGraph';
import type {
    CadTransaction,
    ChangeSet,
    DirtyScope,
    EntityPatch,
    HistoryApplyDirection,
    HistoryEntitySnapshot,
    SnapshotScope,
} from '../types';
import {
    createChangeSet,
} from '../ChangeSetBuilder';
import {
    resolveEntityPatchOperation,
} from '../types';

/**
 * 历史事务应用函数。
 */
export type ApplyHistoryTransaction = (
    transaction: CadTransaction,
    direction: HistoryApplyDirection,
) => void;

/**
 * 历史事务丢弃原因。
 */
export type DiscardHistoryTransactionReason =
    | 'redo-branch-cleared'
    | 'history-cleared';

/**
 * 历史事务丢弃通知函数。
 */
export type DiscardHistoryTransactions = (
    transactions: readonly CadTransaction[],
    reason: DiscardHistoryTransactionReason,
) => void;

/**
 * 模型历史管理器配置。
 */
export interface ModelHistoryManagerOptions {
    /** adapter 注册表；用于判断空事务语义。 */
    readonly adapterRegistry?: HistoryDomainAdapterRegistry;
    /** 事务应用函数；undo / redo 时先调用，成功后才移动栈。 */
    readonly applyTransaction?: ApplyHistoryTransaction;
    /** 事务从内存历史栈永久丢弃时的释放回调。 */
    readonly onDiscardTransactions?: DiscardHistoryTransactions;
}

/**
 * 模型历史状态快照。
 */
export interface ModelHistoryState {
    /** undo 栈。 */
    readonly undoStack: readonly CadTransaction[];
    /** redo 栈。 */
    readonly redoStack: readonly CadTransaction[];
    /** 是否可 undo。 */
    readonly canUndo: boolean;
    /** 是否可 redo。 */
    readonly canRedo: boolean;
}

/**
 * 模型历史管理器。
 */
export interface ModelHistoryManager {
    /**
     * 提交事务。
     *
     * @param transaction 待提交事务。
     * @returns 实际入栈事务；空事务返回 `null`。
     */
    readonly commit: (transaction: CadTransaction) => CadTransaction | null;
    /**
     * 撤销最近模型事务。
     *
     * @returns 被撤销事务；无事务时返回 `null`。
     */
    readonly undo: () => CadTransaction | null;
    /**
     * 重做最近撤销事务。
     *
     * @returns 被重做事务；无事务时返回 `null`。
     */
    readonly redo: () => CadTransaction | null;
    /**
     * 清空历史。
     */
    readonly clear: () => void;
    /**
     * 读取历史状态。
     *
     * @returns 历史状态。
     */
    readonly getState: () => ModelHistoryState;
}

/**
 * 合并去重字符串数组。
 *
 * @param groups 字符串数组集合。
 * @returns 合并后的数组。
 */
function mergeStringArrays(groups: readonly (readonly string[] | undefined)[]): readonly string[] {
    const values = new Set<string>();

    groups.forEach((group) => {
        group?.forEach((value) => values.add(value));
    });

    return Array.from(values);
}

/**
 * 判断快照是否都不存在。
 *
 * @param left 左快照。
 * @param right 右快照。
 * @returns 是否都不存在。
 */
function areBothSnapshotsMissing(
    left: HistoryEntitySnapshot,
    right: HistoryEntitySnapshot,
): boolean {
    return !left.exists && !right.exists;
}

/**
 * 判断实体补丁是否为空补丁。
 *
 * @param patch 实体补丁。
 * @param adapterRegistry adapter 注册表。
 * @returns 是否为空补丁。
 */
function isEntityPatchEmpty(
    patch: EntityPatch,
    adapterRegistry?: HistoryDomainAdapterRegistry,
): boolean {
    if (patch.operation === 'noop') {
        return true;
    }

    if (areBothSnapshotsMissing(patch.before, patch.after)) {
        return true;
    }

    if (patch.before.exists !== patch.after.exists) {
        return false;
    }

    const adapter = adapterRegistry?.get(patch.adapterId);

    if (!adapter?.areSnapshotsEqual) {
        return false;
    }

    return adapter.areSnapshotsEqual(patch.before, patch.after, patch);
}

/**
 * 判断变更集是否为空。
 *
 * @param changeSet 变更集。
 * @param adapterRegistry adapter 注册表。
 * @returns 是否为空。
 */
export function isChangeSetEmpty(
    changeSet: ChangeSet,
    adapterRegistry?: HistoryDomainAdapterRegistry,
): boolean {
    if (changeSet.patches.length === 0) {
        return true;
    }

    return changeSet.patches.every((patch) => isEntityPatchEmpty(patch, adapterRegistry));
}

/**
 * 合并补丁元数据。
 *
 * @param left 左侧补丁。
 * @param right 右侧补丁。
 * @returns 合并后的补丁元数据。
 */
function mergePatchMetadata(left: EntityPatch, right: EntityPatch): EntityPatch['metadata'] {
    const metadata = {
        ...left.metadata,
        ...right.metadata,
        topologyObjectIds: mergeStringArrays([
            left.metadata?.topologyObjectIds,
            right.metadata?.topologyObjectIds,
        ]),
        renderObjectIds: mergeStringArrays([
            left.metadata?.renderObjectIds,
            right.metadata?.renderObjectIds,
        ]),
    };

    if (
        !metadata.sketchId
        && !metadata.featureId
        && !metadata.assemblyId
        && !metadata.componentInstanceId
        && metadata.topologyObjectIds.length === 0
        && metadata.renderObjectIds.length === 0
    ) {
        return undefined;
    }

    return metadata;
}

/**
 * 创建补丁合并 key。
 *
 * @param patch 实体补丁。
 * @returns 合并 key。
 */
function createPatchMergeKey(patch: EntityPatch): string {
    return `${patch.adapterId}:${patch.entityKind}:${patch.entityId}`;
}

/**
 * 合并同一实体补丁。
 *
 * @param left 旧补丁。
 * @param right 新补丁。
 * @returns 合并补丁。
 */
function mergeEntityPatch(left: EntityPatch, right: EntityPatch): EntityPatch {
    if (left.entityKind !== right.entityKind || left.adapterId !== right.adapterId || left.entityId !== right.entityId) {
        throw new Error(`不能合并不同实体补丁：${left.entityId} / ${right.entityId}`);
    }

    const before = left.before;
    const after = right.after;

    return {
        entityKind: left.entityKind,
        entityId: left.entityId,
        adapterId: left.adapterId,
        operation: resolveEntityPatchOperation(before, after),
        before,
        after,
        metadata: mergePatchMetadata(left, right),
    };
}

/**
 * 合并变更集。
 *
 * @param left 旧变更集。
 * @param right 新变更集。
 * @returns 合并后的变更集。
 */
function mergeChangeSets(left: ChangeSet, right: ChangeSet): ChangeSet {
    const patchMap = new Map<string, EntityPatch>();

    left.patches.forEach((patch) => {
        patchMap.set(createPatchMergeKey(patch), patch);
    });

    right.patches.forEach((patch) => {
        const key = createPatchMergeKey(patch);
        const existingPatch = patchMap.get(key);

        patchMap.set(key, existingPatch ? mergeEntityPatch(existingPatch, patch) : patch);
    });

    return createChangeSet(Array.from(patchMap.values()));
}

/**
 * 合并快照范围。
 *
 * @param left 左侧范围。
 * @param right 右侧范围。
 * @returns 合并后的范围。
 */
function mergeSnapshotScopes(left: SnapshotScope, right: SnapshotScope): SnapshotScope {
    return {
        entityIds: mergeStringArrays([left.entityIds, right.entityIds]),
        solverIslandIds: mergeStringArrays([left.solverIslandIds, right.solverIslandIds]),
        includeWorkerCommandHistory: left.includeWorkerCommandHistory || right.includeWorkerCommandHistory,
    };
}

/**
 * 合并模型事务。
 *
 * @param left 旧事务。
 * @param right 新事务。
 * @returns 合并后的事务。
 */
function mergeTransactions(left: CadTransaction, right: CadTransaction): CadTransaction {
    const changeSet = mergeChangeSets(left.changeSet, right.changeSet);
    const dirtyScope: DirtyScope = mergeDirtyScopes([left.dirtyScope, right.dirtyScope]);

    return {
        ...left,
        historySeq: right.historySeq,
        documentRevision: right.documentRevision ?? left.documentRevision,
        snapshotScope: mergeSnapshotScopes(left.snapshotScope, right.snapshotScope),
        dirtyScope,
        changeSet,
        status: 'committed',
    };
}

/**
 * 事务是否可以和上一事务合并。
 *
 * @param left 栈顶事务。
 * @param right 新事务。
 * @returns 是否可合并。
 */
function canMergeTransactions(left: CadTransaction | undefined, right: CadTransaction): boolean {
    return Boolean(
        left
        && left.mergeKey
        && right.mergeKey
        && left.mergeKey === right.mergeKey
        && left.documentId === right.documentId,
    );
}

/**
 * 创建模型历史管理器。
 *
 * @param options 管理器配置。
 * @returns 模型历史管理器。
 */
export function createModelHistoryManager(options: ModelHistoryManagerOptions = {}): ModelHistoryManager {
    const undoStack: CadTransaction[] = [];
    const redoStack: CadTransaction[] = [];
    const applyTransaction = options.applyTransaction ?? (() => undefined);
    const notifyDiscardTransactions = (
        transactions: readonly CadTransaction[],
        reason: DiscardHistoryTransactionReason,
    ): void => {
        if (transactions.length === 0) {
            return;
        }
        options.onDiscardTransactions?.(transactions, reason);
    };

    return {
        commit(transaction) {
            const committedTransaction: CadTransaction = {
                ...transaction,
                status: 'committed',
            };

            if (isChangeSetEmpty(committedTransaction.changeSet, options.adapterRegistry)) {
                return null;
            }

            const previousTransaction = undoStack[undoStack.length - 1];

            if (canMergeTransactions(previousTransaction, committedTransaction)) {
                const mergedTransaction = mergeTransactions(previousTransaction, committedTransaction);
                notifyDiscardTransactions([...redoStack], 'redo-branch-cleared');
                redoStack.length = 0;

                if (isChangeSetEmpty(mergedTransaction.changeSet, options.adapterRegistry)) {
                    undoStack.pop();

                    return null;
                }

                undoStack[undoStack.length - 1] = mergedTransaction;

                return undoStack[undoStack.length - 1];
            }

            undoStack.push(committedTransaction);
            notifyDiscardTransactions([...redoStack], 'redo-branch-cleared');
            redoStack.length = 0;

            return committedTransaction;
        },
        undo() {
            const transaction = undoStack[undoStack.length - 1];

            if (!transaction) {
                return null;
            }

            applyTransaction(transaction, 'undo');
            undoStack.pop();
            redoStack.push(transaction);

            return transaction;
        },
        redo() {
            const transaction = redoStack[redoStack.length - 1];

            if (!transaction) {
                return null;
            }

            applyTransaction(transaction, 'redo');
            redoStack.pop();
            undoStack.push(transaction);

            return transaction;
        },
        clear() {
            notifyDiscardTransactions([...undoStack, ...redoStack], 'history-cleared');
            undoStack.length = 0;
            redoStack.length = 0;
        },
        getState() {
            return {
                undoStack: [...undoStack],
                redoStack: [...redoStack],
                canUndo: undoStack.length > 0,
                canRedo: redoStack.length > 0,
            };
        },
    };
}
